import React, { useCallback, useMemo } from 'react'
import { useTableContext } from './TableProvider'
import { Button, Dropdown, MenuProps, Spin, notification } from 'antd'
import { DownloadIcon } from 'src/assets/icons'
import { useTranslation } from 'react-i18next'
import { defaultPagination } from '../table.constants'
import { LoadingOutlined } from '@ant-design/icons'
import { transferToBlob } from 'src/app/shared/utils'
import saveAs from 'file-saver'
import { ICommonObject } from 'src/app/shared/models/common.model'
import { getDownloadPdf, getDownloadStatus } from 'src/app/shared/services/TransactionService'

const FILTER_OPTIONS = {
  PDF: 2,
  XLSX: 1,
}

interface IGetExportPayload {
  type: string
  pageSize: number | undefined
  pageIndex: number
  columns: string[]
  pageName: string
  searchTerm: string
  searchColumns: string[]
  sortColumns: string[]
  sortColumnsDictionary: ICommonObject
}

const TableDownloadButton: React.FC = () => {
  const { t } = useTranslation()

  const {
    displayDownloadBtn = true,
    disableTableControl = false,
    chosenColumns,
    downloadDropdownItems,
    tableSearchData = {},
    total,
    pageName,
    handleDownload,
    getHeaderName,
    getDownloadStatus: contextGetDownloadStatus,
    getDownloadFile: contextGetDownloadFile,
  } = useTableContext()

  const [api, contextHolder] = notification.useNotification()

  const showExporting = useCallback(() => {
    api.info({
      message: t('common:exporting'),
      placement: 'topRight',
      duration: 3000,
      icon: (
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} size='small' style={{ fontSize: '20px' }} />
      ),
      style: {
        backgroundColor: '#F5FBFF',
        borderRadius: '8px',
        padding: '12px 16px',
        border: '1px solid #43BBFF',
        boxShadow: 'none',
        height: '50px',
        width: '250px',
        display: 'flex',
        alignItems: 'stretch',
      },
    })
  }, [api, t])

  const showExportingInProgress = useCallback(() => {
  api.info({
    message: (
      <div
        style={{
          whiteSpace: 'pre-wrap',
          padding: '5px 5px',
          fontSize: '14px',
          lineHeight: '20px',
          color: '#000',
        }}
      >
        {t('common:exportingInProgress')}
      </div>
    ),
    placement: 'topRight',
    duration: 3000,
    icon: (
      <Spin
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
        size="small"
        style={{ fontSize: '20px' }}
      />
    ),
    style: {
      backgroundColor: '#F5FBFF',
      borderRadius: '8px',
      border: '1px solid #43BBFF',
      boxShadow: 'none',
      height: 'auto',
      width: 'auto',
      display: 'flex',
      alignItems: 'center', 
    },
  })
}, [api, t])

  const showExportingError = useCallback(
    (error: any) => {
      api.error({
        message: t('common:download_failed'),
        description: `${JSON.stringify(error)}`,
        placement: 'topRight',
        duration: 3,
      })
    },
    [api, t],
  )

  const getExportPayload = useCallback(
    (payload: IGetExportPayload) => {
      const {
        type,
        pageIndex,
        pageSize,
        columns,
        pageName,
        searchTerm,
        searchColumns,
        sortColumns,
        sortColumnsDictionary,
      } = payload

      return {
        searchTerm,
        searchColumns,
        sortColumns,
        pageSize,
        pageIndex,
        filterOption: type === 'pdf' ? FILTER_OPTIONS.PDF : FILTER_OPTIONS.XLSX,
        sortColumnsDictionary,
        export: {
          chosenColumnNameList: columns.reduce((acc: any, column) => {
            acc[column] = getHeaderName?.(column)
            return acc
          }, {}),
          pageName,
          header: 'Sunwest Bank',
        },
      }
    },
    [getHeaderName],
  )

  const formatData = useCallback(
    (type: 'pdf' | 'xlsx') => {
      const payload = getExportPayload({
        type: type,
        pageSize: total ?? undefined,
        pageIndex: defaultPagination,
        columns: chosenColumns ?? [],
        pageName: pageName ?? '',
        searchTerm: tableSearchData.searchTerm ?? '',
        searchColumns: tableSearchData.searchColumns ?? [],
        sortColumns: tableSearchData.sortColumns ?? [],
        sortColumnsDictionary: tableSearchData.sortColumnsDictionary ?? {},
      })

      return payload
    },
    [
      chosenColumns,
      getExportPayload,
      pageName,
      tableSearchData.searchColumns,
      tableSearchData.searchTerm,
      tableSearchData.sortColumns,
      tableSearchData.sortColumnsDictionary,
      total,
    ],
  )

  const download = useCallback(
    async (type: 'pdf' | 'xlsx') => {
      try {
        const payload = formatData(type)
        showExporting()
        const result = await handleDownload?.(payload)
        //download binary file
        if ((result?.exportData?.content && result?.exportData?.fileName) || (result?.data?.exportData?.content && result?.data?.exportData?.fileName)) {
          api.destroy()
          const blobData = result?.exportData?.content || result?.data?.exportData?.content
          const fileName = result?.exportData?.fileName || result?.data?.exportData?.fileName
          const blob = transferToBlob(blobData)
          saveAs(blob, fileName)
        }
        else if (result?.fileId || result?.data?.fileId) //polling and download the large report file in azure storage
        {
          api.destroy()
          showExportingInProgress()
          const fileId = result?.fileId || result?.data?.fileId;
          const pollingInterval = 30000; // 30 seconds
          const maxAttempts = 10; // allow the file for 5 mins to be ready, we can increase interval or attemps
          let attempts = 0
          const pollExportStatus = async () => {
            try {
              const getStatusFn = contextGetDownloadStatus || getDownloadStatus;
              const getFileFn = contextGetDownloadFile || getDownloadPdf;
              
              const statusResponse = await getStatusFn(fileId);
              const status = statusResponse?.data;

              if (status === 'Success') {
                // Download the file once it's ready
                const downloadResult = await getFileFn(fileId);

                const blobData = downloadResult?.data?.content
                const fileName = downloadResult?.data?.fileName
                const blob = transferToBlob(blobData)
                saveAs(blob, fileName)
                api.destroy();
              } else if (status === 'failed' || attempts >= maxAttempts) {
                showExportingError(new Error('Export failed or timed out'));
                api.destroy();
              } else {
                attempts++;
                setTimeout(pollExportStatus, pollingInterval);
              }
            } catch (err) {
              showExportingError(err);
            }
          };
          // Start polling
          pollExportStatus();
        }
      } catch (error) {
        api.destroy()
        setTimeout(() => {
          showExportingError(error)
        }, 500)
      }
    },
    [api, formatData, handleDownload, showExporting, showExportingError, showExportingInProgress, contextGetDownloadStatus, contextGetDownloadFile],
  )

  // table download states
  const items: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'pdf',
        label: t('OverallFunctionality:table.export.export_pdf_title'),
        onClick: async () => await download('pdf'),
      },
      {
        key: 'excel',
        label: t('OverallFunctionality:table.export.export_excel_title'),
        onClick: async () => await download('xlsx'),
      },
    ],
    [download, t],
  )

  if (!displayDownloadBtn || disableTableControl) {
    return <React.Fragment />
  }

  return (
    <>
      {contextHolder}
      <Dropdown trigger={['click']} menu={{ items: downloadDropdownItems || items }}>
        <Button id='download-button' icon={<DownloadIcon />} />
      </Dropdown>
    </>
  )
}

export default TableDownloadButton
